import { faPlus, faEdit, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useState, useEffect } from 'react'

import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'

const OHRequestForm = ({ requestData, orgInfo, onClose }) => {

    const { setShowLoader, createInfo } = useAuth()
    const { auth } = useAuth()

    const axios = useAxiosPrivate()

    const [schoolPostcode, setSchoolPostcode] = useState(orgInfo.postcode)
    const [schoolName, setSchoolName] = useState(orgInfo.name)
    const [editMode, setEditMode] = useState(false)
    const [value, setValue] = useState({})

    const [question1Open, setQuestion1Open] = useState(true)
    const [question2Open, setQuestion2Open] = useState(false)
    const [question3Open, setQuestion3Open] = useState(false)
    const [question4Open, setQuestion4Open] = useState(false)
    const [question5Open, setQuestion5Open] = useState(false)
    const [question6Open, setQuestion6Open] = useState(false)
    const [employerConsentOpen, setEmployerConsentOpen] = useState(false)
    const [purposeOpen, setPurposeOpen] = useState(false)
    const [staffDetailsOpen, setStaffDetailsOpen] = useState(true)

    const [textareaFocused, setTextareaFocused] = useState({})
    const [showValidationTooltip, setShowValidationTooltip] = useState(false)

    // Declaration checkboxes state
    const [purposeExplained, setPurposeExplained] = useState(false)
    const [questionsAware, setQuestionsAware] = useState(false)
    const [abandonment, setAbandonment] = useState(false)

    useEffect(() => {
        setSchoolName(orgInfo.name)
        setSchoolPostcode(orgInfo.postcode)
    }, [orgInfo])

    useEffect(() => {
        if (purposeExplained && questionsAware && abandonment) {
            setEmployerConsentOpen(false)
        }
    }, [purposeExplained, questionsAware, abandonment])

    useEffect(() => {
        if (requestData) {
            const requestInfo =
                typeof requestData.request_information === 'string'
                    ? JSON.parse(requestData.request_information)
                    : requestData.request_information
            const nameParts = requestInfo.staff_details.staff_name?.split(' ') || []
            setValue({
                staffName: requestInfo.staff_details.staff_name,
                firstName: nameParts[0] || '',
                lastName: nameParts.slice(1).join(' ') || '',
                staffEmail: requestInfo.staff_details.staff_email,
                dateOfBirth: requestInfo.staff_details.date_of_birth || '',
                phoneNumber: requestInfo.staff_details.phone_number || '',
                startDate: requestInfo.staff_details.start_date,
                role: '',
                description: requestData.description,
            })
        } else {
            setValue({
                staffName: '',
                firstName: '',
                lastName: '',
                staffEmail: '',
                dateOfBirth: '',
                phoneNumber: '',
                startDate: '',
                role: '',
                description: '',
            })
        }
    }, [requestData])

    useEffect(() => {
        // Auto-close Staff Details section when complete and unfocused
        if (isStaffDetailsComplete() && staffDetailsOpen) {
            const isAnyStaffFieldFocused = textareaFocused.firstName || textareaFocused.lastName || textareaFocused.staffEmail || textareaFocused.dateOfBirth || textareaFocused.phoneNumber || textareaFocused.role
            if (!isAnyStaffFieldFocused) {
                setStaffDetailsOpen(false)
                if (!isQuestionComplete(1)) {
                    setQuestion1Open(true)
                }
            }
        }

        if (isQuestionComplete(1) && question1Open) {
            const isTextareaFocused = textareaFocused.supportReasonOther || textareaFocused.longAbsenceDuration
            if (!isTextareaCompletion(1) || !isTextareaFocused) {
                setQuestion1Open(false)
                if (!isQuestionComplete(2)) {
                    setQuestion2Open(true)
                }
            }
        }
        if (isQuestionComplete(2) && question2Open) {
            const isAnyFieldFocused = textareaFocused.issueNatureOther || textareaFocused.mentalHealthOther || textareaFocused.neurodiversityOther || textareaFocused.physicalHealthOther
            if (!isTextareaCompletion(2) || !isAnyFieldFocused) {
                setQuestion2Open(false)
                if (!isQuestionComplete(3)) {
                    setQuestion3Open(true)
                }
            }
        }
        if (isQuestionComplete(3) && question3Open) {
            if (!isTextareaCompletion(3) || (!textareaFocused.meetingOther && !textareaFocused.outcomeOther)) {
                setQuestion3Open(false)
                if (!isQuestionComplete(4)) {
                    setQuestion4Open(true)
                }
            }
        }
        if (isQuestionComplete(4) && question4Open) {
            if (!isTextareaCompletion(4) || !textareaFocused.actionsOther) {
                setQuestion4Open(false)
                if (!isQuestionComplete(5)) {
                    setQuestion5Open(true)
                }
            }
        }
        if (isQuestionComplete(5) && question5Open) {
            setQuestion5Open(false)
            // Open both question 6 and employer consent together
            setQuestion6Open(true)
            setEmployerConsentOpen(true)
        }
    }, [value, textareaFocused])

    const toggleEditMode = () => {
        setEditMode(!editMode)
    }

    const clearFields = () => {
        setValue({})
        setPurposeExplained(false)
        setQuestionsAware(false)
        setAbandonment(false)
    }

    const isFormValid = () => {
        const hasStaffInfo = value?.firstName?.length > 0 &&
            value?.lastName?.length > 0 &&
            value?.staffEmail?.length > 0 &&
            value?.dateOfBirth?.length > 0 &&
            value?.phoneNumber?.length > 0 &&
            value?.role?.length > 0

        const hasDeclarations = purposeExplained && questionsAware && abandonment

        const allQuestionsComplete = isQuestionComplete(1) &&
            isQuestionComplete(2) &&
            isQuestionComplete(3) &&
            isQuestionComplete(4) &&
            isQuestionComplete(5)

        return hasStaffInfo && hasDeclarations && allQuestionsComplete
    }

    const handleOptionChange = (fieldName, fieldValue, fieldsToClear = []) => {
        const newValue = { ...value, [fieldName]: fieldValue }

        fieldsToClear.forEach(field => {
            delete newValue[field]
        })

        setValue(newValue)
    }

    const isQuestionComplete = (questionNumber) => {
        switch (questionNumber) {
            case 1:
                if (!value.supportReason) return false
                if (value.supportReason === 'Policy/Attendance triggers') {
                    if (!value.policyTriggerDetail) return false
                    if (value.policyTriggerDetail === 'Long term absence') return !!value.longAbsenceDuration
                    return true
                }
                if (value.supportReason === 'Conduct and Performance triggers') return !!value.conductTriggerDetail
                if (value.supportReason === 'Health and Safety Triggers') return !!value.healthSafetyDetail
                if (value.supportReason === 'Other') return !!value.supportReasonOther
                return true
            case 2:
                if (!value.issueNature) return false
                if (value.issueNature === 'Functional impact on role') return !!value.functionalDetail
                if (value.issueNature === 'Health related factors') {
                    if (!value.healthDetail) return false
                    if (value.healthDetail === 'Mental health') {
                        if (!value.mentalHealthDetail) return false
                        if (value.mentalHealthDetail === 'Other mental health conditions') return !!value.mentalHealthOther
                        return true
                    }
                    if (value.healthDetail === 'Neurodiversity') {
                        if (!value.neurodiversityDetail) return false
                        if (value.neurodiversityDetail === 'Other neurodiversity') return !!value.neurodiversityOther
                        return true
                    }
                    if (value.healthDetail === 'Physical health') {
                        if (!value.physicalHealthDetail) return false
                        if (value.physicalHealthDetail === 'Other medical issue') return !!value.physicalHealthOther
                        return true
                    }
                    return true
                }
                if (value.issueNature === 'Personal circumstances') return !!value.personalDetail
                if (value.issueNature === 'Wellbeing concerns') return !!value.wellbeingDetail
                if (value.issueNature === 'Other factors') return !!value.issueNatureOther
                return true
            case 3:
                if (!value.meetingsHeld || !value.outcomes) return false
                if (value.meetingsHeld === 'Other meetings' && !value.meetingOther) return false
                if (value.outcomes === 'Other outcomes' && !value.outcomeOther) return false
                return true
            case 4:
                if (!value.actionsImplemented) return false
                if (value.actionsImplemented === 'Other') return !!value.actionsOther
                return true
            case 5:
                return !!value.intendedOutcome
            case 6:
                return true // Question 6 is optional, so always considered complete
            default:
                return false
        }
    }

    const isStaffDetailsComplete = () => {
        return !!(value.firstName && value.lastName && value.staffEmail && value.dateOfBirth && value.phoneNumber && value.role)
    }

    const isTextareaCompletion = (questionNumber) => {
        switch (questionNumber) {
            case 1:
                return (value.supportReason === 'Other' && !!value.supportReasonOther) ||
                    (value.supportReason === 'Policy/Attendance triggers' && value.policyTriggerDetail === 'Long term absence' && !!value.longAbsenceDuration)
            case 2:
                return (value.issueNature === 'Other factors' && !!value.issueNatureOther) ||
                    (value.healthDetail === 'Mental health' && value.mentalHealthDetail === 'Other mental health conditions' && !!value.mentalHealthOther) ||
                    (value.healthDetail === 'Neurodiversity' && value.neurodiversityDetail === 'Other neurodiversity' && !!value.neurodiversityOther) ||
                    (value.healthDetail === 'Physical health' && value.physicalHealthDetail === 'Other medical issue' && !!value.physicalHealthOther)
            case 3:
                return (value.meetingsHeld === 'Other meetings' && !!value.meetingOther) ||
                    (value.outcomes === 'Other outcomes' && !!value.outcomeOther)
            case 4:
                return (value.actionsImplemented === 'Other' && !!value.actionsOther)
            default:
                return false
        }
    }

    const getQuestionSummary = (questionNumber) => {
        switch (questionNumber) {
            case 1:
                if (!value.supportReason) return null
                let summary = ''
                if (value.supportReason === 'Policy/Attendance triggers') {
                    summary = 'Policy/attendance'
                    if (value.policyTriggerDetail) {
                        summary += ` - ${value.policyTriggerDetail}`
                        if (value.policyTriggerDetail === 'Long term absence' && value.longAbsenceDuration) {
                            summary += ` (${value.longAbsenceDuration})`
                        }
                    }
                } else if (value.supportReason === 'Conduct and Performance triggers') {
                    summary = 'Conduct/performance'
                    if (value.conductTriggerDetail) summary += ` - ${value.conductTriggerDetail}`
                } else if (value.supportReason === 'Health and Safety Triggers') {
                    summary = 'Health & safety'
                    if (value.healthSafetyDetail) summary += ` - ${value.healthSafetyDetail}`
                } else if (value.supportReason === 'General Advice') {
                    summary = 'General advice'
                } else if (value.supportReason === 'Other') {
                    summary = 'Other'
                    if (value.supportReasonOther) summary += ` - ${value.supportReasonOther}`
                }
                return summary
            case 2:
                if (!value.issueNature) return null
                let summary2 = ''
                if (value.issueNature === 'Functional impact on role') {
                    summary2 = 'Functional impact on role'
                    if (value.functionalDetail) summary2 += ` - ${value.functionalDetail}`
                } else if (value.issueNature === 'Health related factors') {
                    summary2 = 'Health related factors'
                    if (value.healthDetail) {
                        summary2 += ` - ${value.healthDetail}`
                        if (value.healthDetail === 'Mental health' && value.mentalHealthDetail) {
                            summary2 += ` (${value.mentalHealthDetail}`
                            if (value.mentalHealthDetail === 'Other mental health conditions' && value.mentalHealthOther) {
                                summary2 += `: ${value.mentalHealthOther}`
                            }
                            summary2 += ')'
                        } else if (value.healthDetail === 'Neurodiversity' && value.neurodiversityDetail) {
                            summary2 += ` (${value.neurodiversityDetail}`
                            if (value.neurodiversityDetail === 'Other neurodiversity' && value.neurodiversityOther) {
                                summary2 += `: ${value.neurodiversityOther}`
                            }
                            summary2 += ')'
                        } else if (value.healthDetail === 'Physical health' && value.physicalHealthDetail) {
                            summary2 += ` (${value.physicalHealthDetail}`
                            if (value.physicalHealthDetail === 'Other medical issue' && value.physicalHealthOther) {
                                summary2 += `: ${value.physicalHealthOther}`
                            }
                            summary2 += ')'
                        }
                    }
                } else if (value.issueNature === 'Personal circumstances') {
                    summary2 = 'Personal circumstances'
                    if (value.personalDetail) summary2 += ` - ${value.personalDetail}`
                } else if (value.issueNature === 'Wellbeing concerns') {
                    summary2 = 'Wellbeing concerns'
                    if (value.wellbeingDetail) summary2 += ` - ${value.wellbeingDetail}`
                } else if (value.issueNature === 'Other factors') {
                    summary2 = 'Other factors'
                    if (value.issueNatureOther) summary2 += ` - ${value.issueNatureOther}`
                }
                return summary2
            case 3:
                if (!value.meetingsHeld || !value.outcomes) return null
                let summary3 = ''
                if (value.meetingsHeld) {
                    summary3 += `Meetings: ${value.meetingsHeld}`
                    if (value.meetingsHeld === 'Other meetings' && value.meetingOther) {
                        summary3 += ` (${value.meetingOther})`
                    }
                }
                if (value.outcomes) {
                    if (summary3) summary3 += ' | '
                    summary3 += `Outcomes: ${value.outcomes}`
                    if (value.outcomes === 'Other outcomes' && value.outcomeOther) {
                        summary3 += ` (${value.outcomeOther})`
                    }
                }
                return summary3
            case 4:
                if (!value.actionsImplemented) return null
                let summary4 = `Actions: ${value.actionsImplemented}`
                if (value.actionsImplemented === 'Other' && value.actionsOther) {
                    summary4 += ` (${value.actionsOther})`
                }
                return summary4
            case 5:
                if (!value.intendedOutcome) return null
                return `Intended outcome: ${value.intendedOutcome}`
            case 6:
                return 'Additional information provided'
            default:
                return null
        }
    }

    const toggleQuestion = (questionNumber) => {
        switch (questionNumber) {
            case 'purpose':
                setEmployerConsentOpen(!employerConsentOpen)
                break
            case 'employerConsent':
                setEmployerConsentOpen(!employerConsentOpen)
                break
            case 'staffDetails':
                setStaffDetailsOpen(!staffDetailsOpen)
                break
            case 1:
                setQuestion1Open(!question1Open)
                break
            case 2:
                setQuestion2Open(!question2Open)
                break
            case 3:
                setQuestion3Open(!question3Open)
                break
            case 4:
                setQuestion4Open(!question4Open)
                break
            case 5:
                setQuestion5Open(!question5Open)
                break
            case 6:
                setQuestion6Open(!question6Open)
                break
            default:
                break
        }
    }

    const isPurposeComplete = () => {
        return purposeExplained && questionsAware && abandonment
    }

    const getPurposeSummary = () => {
        if (!isPurposeComplete()) return null
        return "All declarations confirmed"
    }

    const submitRequest = async (e) => {
        e.preventDefault()

        if (!isFormValid()) {
            setShowValidationTooltip(true)
            setTimeout(() => {
                setShowValidationTooltip(false)
            }, 3000)
            return
        }

        const form = e.currentTarget
        if (!form.checkValidity()) {
            setShowValidationTooltip(true)
            setTimeout(() => {
                setShowValidationTooltip(false)
            }, 3000)
            return
        }
        setShowLoader(true)

        const formResponses = {
            question1: {
                supportReason: value.supportReason,
                policyTriggerDetail: value.policyTriggerDetail,
                longAbsenceDuration: value.longAbsenceDuration,
                conductTriggerDetail: value.conductTriggerDetail,
                healthSafetyDetail: value.healthSafetyDetail,
                supportReasonOther: value.supportReasonOther
            },
            question2: {
                issueNature: value.issueNature,
                functionalDetail: value.functionalDetail,
                healthDetail: value.healthDetail,
                mentalHealthDetail: value.mentalHealthDetail,
                mentalHealthOther: value.mentalHealthOther,
                neurodiversityDetail: value.neurodiversityDetail,
                neurodiversityOther: value.neurodiversityOther,
                physicalHealthDetail: value.physicalHealthDetail,
                physicalHealthOther: value.physicalHealthOther,
                personalDetail: value.personalDetail,
                wellbeingDetail: value.wellbeingDetail,
                issueNatureOther: value.issueNatureOther
            },
            question3: {
                meetingsHeld: value.meetingsHeld,
                meetingOther: value.meetingOther,
                outcomes: value.outcomes,
                outcomeOther: value.outcomeOther
            },
            question4: {
                actionsImplemented: value.actionsImplemented,
                actionsOther: value.actionsOther
            },
            question5: {
                intendedOutcome: value.intendedOutcome
            },
            question6: {
                additionalInfo: value.additionalInfo
            }
        }

        const requestData = {
            titleId: '13172b95-d9db-431d-957c-7f3049c24f6f',
            staffName: value.staffName,
            staffEmail: value.staffEmail,
            dateOfBirth: value.dateOfBirth,
            phoneNumber: value.phoneNumber,
            role: value.role,
            declarations: {
                purposeExplained,
                questionsAware,
                abandonment
            },
            formResponses: formResponses,
            policyId: auth?.currentPolicy?.policy_id,
            schoolName: schoolName,
            schoolAddress: auth?.schools[0].address,
            schoolPostcode: schoolPostcode,
            orgId: auth.schools[0].id,
            orgUrn: auth.schools[0].urn,
        }

        try {
            const response = await axios.post('/service-requests/oh/create', requestData)

            if (response.status !== 200) {
                console.error('Failed to submit request')
                createInfo('error', 'Failed to submit request, please try again')
            }

            clearFields()
            createInfo('success', 'OH Request created successfully')
            onClose()
        } catch (error) {
            console.error('Error:', error)
            createInfo('error', 'Failed to submit request, please try again')
        } finally {
            setShowLoader(false)
        }
    }

    return (
        <>
            <form onSubmit={submitRequest}>
                <div className="relative flex-1">
                    <div className="px-4 py-2">
                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion('staffDetails')}
                            >
                                <label className="block text-lg font-medium text-[#02ADAD]">
                                    Staff Member Details
                                </label>
                                <span className="text-gray-400">
                                    {staffDetailsOpen ? '▼' : '▶'}
                                </span>
                            </div>
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${staffDetailsOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div className="p-4">
                                    <div className="bg-blue-50 border-l-4 border-blue-400 p-3 mb-4">
                                        <div className="flex">
                                            <div className="flex-shrink-0">
                                                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <div className="ml-3">
                                                <p className="text-sm text-blue-700">
                                                    We collect the staff member's details so our occupational health team can reach out directly if additional information is needed to provide the best possible support and advice.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex justify-between space-x-6">
                                        <div className="w-full">
                                            <label htmlFor="firstName">
                                                First Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="firstName"
                                                required
                                                className="border rounded-md py-2 px-4 w-full mt-1"
                                                onChange={(e) =>
                                                    setValue((prevValue) => ({
                                                        ...prevValue,
                                                        firstName: e.target.value,
                                                        staffName: `${e.target.value} ${prevValue.lastName || ''}`.trim(),
                                                    }))
                                                }
                                                onFocus={() => setTextareaFocused({ ...textareaFocused, firstName: true })}
                                                onBlur={() => setTextareaFocused({ ...textareaFocused, firstName: false })}
                                                value={value.firstName || ''}
                                            />
                                        </div>
                                        <div className="w-full">
                                            <label htmlFor="lastName">
                                                Last Name <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                id="lastName"
                                                required
                                                className="border rounded-md py-2 px-4 w-full mt-1"
                                                onChange={(e) =>
                                                    setValue((prevValue) => ({
                                                        ...prevValue,
                                                        lastName: e.target.value,
                                                        staffName: `${prevValue.firstName || ''} ${e.target.value}`.trim(),
                                                    }))
                                                }
                                                onFocus={() => setTextareaFocused({ ...textareaFocused, lastName: true })}
                                                onBlur={() => setTextareaFocused({ ...textareaFocused, lastName: false })}
                                                value={value.lastName || ''}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex flex-col mt-4">
                                        <label htmlFor="staffEmail">
                                            Email Address <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            id="staffEmail"
                                            className="border rounded-md py-2 px-4 mt-1"
                                            required
                                            onChange={(e) => setValue({ ...value, staffEmail: e.target.value })}
                                            onFocus={() => setTextareaFocused({ ...textareaFocused, staffEmail: true })}
                                            onBlur={() => setTextareaFocused({ ...textareaFocused, staffEmail: false })}
                                            value={value.staffEmail || ''}
                                        />
                                    </div>
                                    <div className="flex flex-col mt-4">
                                        <label htmlFor="dateOfBirth">
                                            Date of Birth <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            id="dateOfBirth"
                                            className="border rounded-md py-2 px-4 mt-1"
                                            required
                                            onChange={(e) => setValue({ ...value, dateOfBirth: e.target.value })}
                                            onFocus={() => setTextareaFocused({ ...textareaFocused, dateOfBirth: true })}
                                            onBlur={() => setTextareaFocused({ ...textareaFocused, dateOfBirth: false })}
                                            value={value.dateOfBirth || ''}
                                        />
                                    </div>
                                    <div className="flex flex-col mt-4">
                                        <label htmlFor="phoneNumber">
                                            Phone Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="tel"
                                            id="phoneNumber"
                                            className="border rounded-md py-2 px-4 mt-1"
                                            required
                                            onChange={(e) => setValue({ ...value, phoneNumber: e.target.value })}
                                            onFocus={() => setTextareaFocused({ ...textareaFocused, phoneNumber: true })}
                                            onBlur={() => setTextareaFocused({ ...textareaFocused, phoneNumber: false })}
                                            value={value.phoneNumber || ''}
                                        />
                                    </div>
                                    <div className="flex flex-col mt-4">
                                        <label htmlFor="role">
                                            Role Title <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            id="role"
                                            className="border rounded-md py-2 px-4 mt-1"
                                            required
                                            onChange={(e) => setValue({ ...value, role: e.target.value })}
                                            onFocus={() => setTextareaFocused({ ...textareaFocused, role: true })}
                                            onBlur={() => setTextareaFocused({ ...textareaFocused, role: false })}
                                            value={value.role || ''}
                                        />
                                    </div>
                                    <div className="text-xs mt-2">
                                        <span className="text-red-500">*</span> Denotes required fields.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion(1)}
                            >
                                <label className="block text-sm font-medium text-gray-700">
                                    Question 1: Why is support being requested at this time? <span className="text-red-500">*</span>
                                    {isQuestionComplete(1) && <span className="ml-2 text-green-600">✓</span>}
                                </label>
                                <span className="text-gray-400">
                                    {question1Open ? '▼' : '▶'}
                                </span>
                            </div>
                            {!question1Open && getQuestionSummary(1) && (
                                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                                    {getQuestionSummary(1)}
                                </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${question1Open ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div>
                                    <div className="p-2">
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="trigger_policy"
                                                    name="supportReason"
                                                    value="Policy/Attendance triggers"
                                                    className="mr-2"
                                                    onChange={(e) => handleOptionChange('supportReason', e.target.value, [
                                                        'policyTriggerDetail', 'conductTriggerDetail', 'healthTriggerDetail',
                                                        'healthSafetyDetail', 'supportReasonOther', 'longAbsenceDuration'
                                                    ])}
                                                    checked={value.supportReason === 'Policy/Attendance triggers'}
                                                />
                                                <label htmlFor="trigger_policy" className="text-sm text-gray-700">Policy/Attendance triggers</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="trigger_conduct"
                                                    name="supportReason"
                                                    value="Conduct and Performance triggers"
                                                    className="mr-2"
                                                    onChange={(e) => handleOptionChange('supportReason', e.target.value, [
                                                        'policyTriggerDetail', 'conductTriggerDetail', 'healthTriggerDetail',
                                                        'healthSafetyDetail', 'supportReasonOther', 'longAbsenceDuration'
                                                    ])}
                                                    checked={value.supportReason === 'Conduct and Performance triggers'}
                                                />
                                                <label htmlFor="trigger_conduct" className="text-sm text-gray-700">Conduct and Performance triggers</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="trigger_health"
                                                    name="supportReason"
                                                    value="Health and Safety Triggers"
                                                    className="mr-2"
                                                    onChange={(e) => handleOptionChange('supportReason', e.target.value, [
                                                        'policyTriggerDetail', 'conductTriggerDetail', 'healthTriggerDetail',
                                                        'healthSafetyDetail', 'supportReasonOther', 'longAbsenceDuration'
                                                    ])}
                                                    checked={value.supportReason === 'Health and Safety Triggers'}
                                                />
                                                <label htmlFor="trigger_health" className="text-sm text-gray-700">Health and Safety Triggers</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="trigger_general"
                                                    name="supportReason"
                                                    value="General Advice"
                                                    className="mr-2"
                                                    onChange={(e) => handleOptionChange('supportReason', e.target.value, [
                                                        'policyTriggerDetail', 'conductTriggerDetail', 'healthTriggerDetail',
                                                        'healthSafetyDetail', 'supportReasonOther', 'longAbsenceDuration'
                                                    ])}
                                                    checked={value.supportReason === 'General Advice'}
                                                />
                                                <label htmlFor="trigger_general" className="text-sm text-gray-700">General Advice</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="trigger_other"
                                                    name="supportReason"
                                                    value="Other"
                                                    className="mr-2"
                                                    onChange={(e) => handleOptionChange('supportReason', e.target.value, [
                                                        'policyTriggerDetail', 'conductTriggerDetail', 'healthTriggerDetail',
                                                        'healthSafetyDetail', 'supportReasonOther', 'longAbsenceDuration'
                                                    ])}
                                                    checked={value.supportReason === 'Other'}
                                                />
                                                <label htmlFor="trigger_other" className="text-sm text-gray-700">Other Trigger (Please State)</label>
                                            </div>
                                        </div>
                                        {value.supportReason === 'Other' && (
                                            <div className="mt-2 ml-6">
                                                <textarea
                                                    id="supportReasonOther"
                                                    name="supportReasonOther"
                                                    rows="2"
                                                    className="border rounded-md py-2 px-4 w-full resize-vertical"
                                                    placeholder="Please specify other trigger..."
                                                    onChange={(e) => setValue({ ...value, supportReasonOther: e.target.value })}
                                                    value={value.supportReasonOther || ''}
                                                    onFocus={() => setTextareaFocused({ ...textareaFocused, supportReasonOther: true })}
                                                    onBlur={() => setTextareaFocused({ ...textareaFocused, supportReasonOther: false })}
                                                ></textarea>
                                            </div>
                                        )}
                                    </div>

                                    {value.supportReason === 'Policy/Attendance triggers' && (
                                        <div className="p-2 bg-gray-50 rounded-md">
                                            <div className="space-y-2 pl-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="short_absence"
                                                        name="policyTriggerDetail"
                                                        value="Short term absence"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, policyTriggerDetail: e.target.value })}
                                                        checked={value.policyTriggerDetail === 'Short term absence'}
                                                    />
                                                    <label htmlFor="short_absence" className="text-sm text-gray-700">Short Term Absences</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="long_absence"
                                                        name="policyTriggerDetail"
                                                        value="Long term absence"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, policyTriggerDetail: e.target.value })}
                                                        checked={value.policyTriggerDetail === 'Long term absence'}
                                                    />
                                                    <label htmlFor="long_absence" className="text-sm text-gray-700">Long Term Absences (How Long?)</label>
                                                </div>
                                                {value.policyTriggerDetail === 'Long term absence' && (
                                                    <div className="mt-2 ml-8">
                                                        <input
                                                            type="text"
                                                            id="longAbsenceDuration"
                                                            name="longAbsenceDuration"
                                                            className="border rounded-md py-1 px-2 w-48 text-sm"
                                                            placeholder="e.g. 6 weeks, 3 months"
                                                            onChange={(e) => setValue({ ...value, longAbsenceDuration: e.target.value })}
                                                            value={value.longAbsenceDuration || ''}
                                                            onFocus={() => setTextareaFocused({ ...textareaFocused, longAbsenceDuration: true })}
                                                            onBlur={() => setTextareaFocused({ ...textareaFocused, longAbsenceDuration: false })}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="frequent_absence"
                                                        name="policyTriggerDetail"
                                                        value="Frequent intermittent absence"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, policyTriggerDetail: e.target.value })}
                                                        checked={value.policyTriggerDetail === 'Frequent intermittent absence'}
                                                    />
                                                    <label htmlFor="frequent_absence" className="text-sm text-gray-700">Frequent Intermittent Absence</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="health_attendance"
                                                        name="policyTriggerDetail"
                                                        value="Health issue impacting attendance"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, policyTriggerDetail: e.target.value })}
                                                        checked={value.policyTriggerDetail === 'Health issue impacting attendance'}
                                                    />
                                                    <label htmlFor="health_attendance" className="text-sm text-gray-700">Health Issue Impacting Attendance</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="return_work"
                                                        name="policyTriggerDetail"
                                                        value="Return to work after absence"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, policyTriggerDetail: e.target.value })}
                                                        checked={value.policyTriggerDetail === 'Return to work after absence'}
                                                    />
                                                    <label htmlFor="return_work" className="text-sm text-gray-700">Return To Work After Absence</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {value.supportReason === 'Conduct and Performance triggers' && (
                                        <div className="p-2 bg-gray-50 rounded-md">
                                            <div className="space-y-2 pl-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="performance_concerns"
                                                        name="conductTriggerDetail"
                                                        value="Performance concerns"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, conductTriggerDetail: e.target.value })}
                                                        checked={value.conductTriggerDetail === 'Performance concerns'}
                                                    />
                                                    <label htmlFor="performance_concerns" className="text-sm text-gray-700">Performance Concerns</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="behavioral_concerns"
                                                        name="conductTriggerDetail"
                                                        value="Behavioral concerns"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, conductTriggerDetail: e.target.value })}
                                                        checked={value.conductTriggerDetail === 'Behavioral concerns'}
                                                    />
                                                    <label htmlFor="behavioral_concerns" className="text-sm text-gray-700">Behavioral Concerns</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="escalation_required"
                                                        name="conductTriggerDetail"
                                                        value="Escalation required by HR or school policy"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, conductTriggerDetail: e.target.value })}
                                                        checked={value.conductTriggerDetail === 'Escalation required by HR or school policy'}
                                                    />
                                                    <label htmlFor="escalation_required" className="text-sm text-gray-700">Escalation Required By HR Or School Policy</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {value.supportReason === 'Health and Safety Triggers' && (
                                        <div className="p-2 bg-gray-50 rounded-md">
                                            <div className="space-y-2 pl-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="safety_concern"
                                                        name="healthSafetyDetail"
                                                        value="Health and safety concern following risk assessment"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, healthSafetyDetail: e.target.value })}
                                                        checked={value.healthSafetyDetail === 'Health and safety concern following risk assessment'}
                                                    />
                                                    <label htmlFor="safety_concern" className="text-sm text-gray-700">Health And Safety Concern Following Risk Assessment</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion(2)}
                            >
                                <label className="block text-sm font-medium text-gray-700">
                                    Question 2: What is the main nature of the employee issue? <span className="text-red-500">*</span>
                                    {isQuestionComplete(2) && <span className="ml-2 text-green-600">✓</span>}
                                </label>
                                <span className="text-gray-400">
                                    {question2Open ? '▼' : '▶'}
                                </span>
                            </div>
                            {!question2Open && getQuestionSummary(2) && (
                                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                                    {getQuestionSummary(2)}
                                </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${question2Open ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div>
                                    <div className="p-2">
                                        <div className="space-y-2">
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="functional_impact"
                                                    name="issueNature"
                                                    value="Functional impact on role"
                                                    className="mr-2"
                                                    onChange={(e) => {
                                                        const newValue = { ...value, issueNature: e.target.value }
                                                        delete newValue.functionalDetail
                                                        delete newValue.personalDetail
                                                        delete newValue.wellbeingDetail
                                                        delete newValue.issueNatureOther
                                                        delete newValue.healthDetail
                                                        delete newValue.mentalHealthDetail
                                                        delete newValue.neurodiversityDetail
                                                        delete newValue.physicalHealthDetail
                                                        delete newValue.mentalHealthOther
                                                        delete newValue.neurodiversityOther
                                                        delete newValue.physicalHealthOther
                                                        setValue(newValue)
                                                    }}
                                                    checked={value.issueNature === 'Functional impact on role'}
                                                />
                                                <label htmlFor="functional_impact" className="text-sm text-gray-700">Functional Impact On Role</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="health_factors"
                                                    name="issueNature"
                                                    value="Health related factors"
                                                    className="mr-2"
                                                    onChange={(e) => {
                                                        const newValue = { ...value, issueNature: e.target.value }
                                                        delete newValue.functionalDetail
                                                        delete newValue.personalDetail
                                                        delete newValue.wellbeingDetail
                                                        delete newValue.issueNatureOther
                                                        delete newValue.healthDetail
                                                        delete newValue.mentalHealthDetail
                                                        delete newValue.neurodiversityDetail
                                                        delete newValue.physicalHealthDetail
                                                        delete newValue.mentalHealthOther
                                                        delete newValue.neurodiversityOther
                                                        delete newValue.physicalHealthOther
                                                        setValue(newValue)
                                                    }}
                                                    checked={value.issueNature === 'Health related factors'}
                                                />
                                                <label htmlFor="health_factors" className="text-sm text-gray-700">Health Related Factors</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="personal_circumstances"
                                                    name="issueNature"
                                                    value="Personal circumstances"
                                                    className="mr-2"
                                                    onChange={(e) => {
                                                        const newValue = { ...value, issueNature: e.target.value }
                                                        delete newValue.functionalDetail
                                                        delete newValue.personalDetail
                                                        delete newValue.wellbeingDetail
                                                        delete newValue.issueNatureOther
                                                        delete newValue.healthDetail
                                                        delete newValue.mentalHealthDetail
                                                        delete newValue.neurodiversityDetail
                                                        delete newValue.physicalHealthDetail
                                                        delete newValue.mentalHealthOther
                                                        delete newValue.neurodiversityOther
                                                        delete newValue.physicalHealthOther
                                                        setValue(newValue)
                                                    }}
                                                    checked={value.issueNature === 'Personal circumstances'}
                                                />
                                                <label htmlFor="personal_circumstances" className="text-sm text-gray-700">Personal Circumstances</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="wellbeing_concerns"
                                                    name="issueNature"
                                                    value="Wellbeing concerns"
                                                    className="mr-2"
                                                    onChange={(e) => {
                                                        const newValue = { ...value, issueNature: e.target.value }
                                                        delete newValue.functionalDetail
                                                        delete newValue.personalDetail
                                                        delete newValue.wellbeingDetail
                                                        delete newValue.issueNatureOther
                                                        delete newValue.healthDetail
                                                        delete newValue.mentalHealthDetail
                                                        delete newValue.neurodiversityDetail
                                                        delete newValue.physicalHealthDetail
                                                        delete newValue.mentalHealthOther
                                                        delete newValue.neurodiversityOther
                                                        delete newValue.physicalHealthOther
                                                        setValue(newValue)
                                                    }}
                                                    checked={value.issueNature === 'Wellbeing concerns'}
                                                />
                                                <label htmlFor="wellbeing_concerns" className="text-sm text-gray-700">Wellbeing Concerns</label>
                                            </div>
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="other_factors"
                                                    name="issueNature"
                                                    value="Other factors"
                                                    className="mr-2"
                                                    onChange={(e) => {
                                                        const newValue = { ...value, issueNature: e.target.value }
                                                        delete newValue.functionalDetail
                                                        delete newValue.personalDetail
                                                        delete newValue.wellbeingDetail
                                                        delete newValue.issueNatureOther
                                                        delete newValue.healthDetail
                                                        delete newValue.mentalHealthDetail
                                                        delete newValue.neurodiversityDetail
                                                        delete newValue.physicalHealthDetail
                                                        delete newValue.mentalHealthOther
                                                        delete newValue.neurodiversityOther
                                                        delete newValue.physicalHealthOther
                                                        setValue(newValue)
                                                    }}
                                                    checked={value.issueNature === 'Other factors'}
                                                />
                                                <label htmlFor="other_factors" className="text-sm text-gray-700">Other Factors (Please State)</label>
                                            </div>
                                        </div>
                                        {value.issueNature === 'Other factors' && (
                                            <div className="mt-2 ml-6">
                                                <textarea
                                                    id="issueNatureOther"
                                                    name="issueNatureOther"
                                                    rows="2"
                                                    className="border rounded-md py-2 px-4 w-full resize-vertical"
                                                    placeholder="Please specify other factors..."
                                                    onChange={(e) => setValue({ ...value, issueNatureOther: e.target.value })}
                                                    value={value.issueNatureOther || ''}
                                                    onFocus={() => setTextareaFocused({ ...textareaFocused, issueNatureOther: true })}
                                                    onBlur={() => setTextareaFocused({ ...textareaFocused, issueNatureOther: false })}
                                                ></textarea>
                                            </div>
                                        )}
                                    </div>

                                    {value.issueNature === 'Functional impact on role' && (
                                        <div className="p-2 bg-gray-50 rounded-md">
                                            <div className="space-y-2 pl-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="reduced_capacity"
                                                        name="functionalDetail"
                                                        value="Reduced Functional Capacity"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, functionalDetail: e.target.value })}
                                                        checked={value.functionalDetail === 'Reduced Functional Capacity'}
                                                    />
                                                    <label htmlFor="reduced_capacity" className="text-sm text-gray-700">Reduced Functional Capacity</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="struggling_demands"
                                                        name="functionalDetail"
                                                        value="Struggling To Meet Demands Of Role"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, functionalDetail: e.target.value })}
                                                        checked={value.functionalDetail === 'Struggling To Meet Demands Of Role'}
                                                    />
                                                    <label htmlFor="struggling_demands" className="text-sm text-gray-700">Struggling To Meet Demands Of Role</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="requires_adjustments"
                                                        name="functionalDetail"
                                                        value="Requires Workplace Adjustments"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, functionalDetail: e.target.value })}
                                                        checked={value.functionalDetail === 'Requires Workplace Adjustments'}
                                                    />
                                                    <label htmlFor="requires_adjustments" className="text-sm text-gray-700">Requires Workplace Adjustments</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="safety_concerns"
                                                        name="functionalDetail"
                                                        value="Safety Concerns"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, functionalDetail: e.target.value })}
                                                        checked={value.functionalDetail === 'Safety Concerns'}
                                                    />
                                                    <label htmlFor="safety_concerns" className="text-sm text-gray-700">Safety Concerns</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}


                                    {value.issueNature === 'Health related factors' && (
                                        <div className="p-2 bg-gray-50 rounded-md">
                                            <div className="space-y-2 pl-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="mental_health"
                                                        name="healthDetail"
                                                        value="Mental health"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, healthDetail: e.target.value }
                                                            delete newValue.mentalHealthDetail
                                                            delete newValue.neurodiversityDetail
                                                            delete newValue.physicalHealthDetail
                                                            delete newValue.mentalHealthOther
                                                            delete newValue.neurodiversityOther
                                                            delete newValue.physicalHealthOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.healthDetail === 'Mental health'}
                                                    />
                                                    <label htmlFor="mental_health" className="text-sm text-gray-700">Mental Health</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="neurodiversity"
                                                        name="healthDetail"
                                                        value="Neurodiversity"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, healthDetail: e.target.value }
                                                            delete newValue.mentalHealthDetail
                                                            delete newValue.neurodiversityDetail
                                                            delete newValue.physicalHealthDetail
                                                            delete newValue.mentalHealthOther
                                                            delete newValue.neurodiversityOther
                                                            delete newValue.physicalHealthOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.healthDetail === 'Neurodiversity'}
                                                    />
                                                    <label htmlFor="neurodiversity" className="text-sm text-gray-700">Neurodiversity</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="physical_health"
                                                        name="healthDetail"
                                                        value="Physical health"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, healthDetail: e.target.value }
                                                            delete newValue.mentalHealthDetail
                                                            delete newValue.neurodiversityDetail
                                                            delete newValue.physicalHealthDetail
                                                            delete newValue.mentalHealthOther
                                                            delete newValue.neurodiversityOther
                                                            delete newValue.physicalHealthOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.healthDetail === 'Physical health'}
                                                    />
                                                    <label htmlFor="physical_health" className="text-sm text-gray-700">Physical Health</label>
                                                </div>
                                            </div>

                                            {value.healthDetail === 'Mental health' && (
                                                <div className="mt-2">
                                                    <select
                                                        name="mentalHealthDetail"
                                                        value={value.mentalHealthDetail || ''}
                                                        onChange={(e) => setValue({ ...value, mentalHealthDetail: e.target.value })}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    >
                                                        <option value="">Please select...</option>
                                                        <option value="Anxiety/depression">Anxiety/depression</option>
                                                        <option value="Stress/burnout">Stress/burnout</option>
                                                        <option value="Other mental health conditions">Other mental health conditions (please state)</option>
                                                    </select>
                                                    {value.mentalHealthDetail === 'Other mental health conditions' && (
                                                        <div className="mt-2">
                                                            <input
                                                                type="text"
                                                                id="mentalHealthOther"
                                                                name="mentalHealthOther"
                                                                className="border rounded-md py-1 px-2 w-full text-sm"
                                                                placeholder="Please specify other mental health condition..."
                                                                onChange={(e) => setValue({ ...value, mentalHealthOther: e.target.value })}
                                                                value={value.mentalHealthOther || ''}
                                                                onFocus={() => setTextareaFocused({ ...textareaFocused, mentalHealthOther: true })}
                                                                onBlur={() => setTextareaFocused({ ...textareaFocused, mentalHealthOther: false })}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}


                                            {value.healthDetail === 'Neurodiversity' && (
                                                <div className="mt-2">
                                                    <select
                                                        name="neurodiversityDetail"
                                                        value={value.neurodiversityDetail || ''}
                                                        onChange={(e) => setValue({ ...value, neurodiversityDetail: e.target.value })}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    >
                                                        <option value="">Please select...</option>
                                                        <option value="ADHD">ADHD</option>
                                                        <option value="Autism">Autism</option>
                                                        <option value="Other neurodiversity">Other neurodiversity (please state)</option>
                                                    </select>
                                                    {value.neurodiversityDetail === 'Other neurodiversity' && (
                                                        <div className="mt-2">
                                                            <input
                                                                type="text"
                                                                id="neurodiversityOther"
                                                                name="neurodiversityOther"
                                                                className="border rounded-md py-1 px-2 w-full text-sm"
                                                                placeholder="Please specify other neurodiversity..."
                                                                onChange={(e) => setValue({ ...value, neurodiversityOther: e.target.value })}
                                                                value={value.neurodiversityOther || ''}
                                                                onFocus={() => setTextareaFocused({ ...textareaFocused, neurodiversityOther: true })}
                                                                onBlur={() => setTextareaFocused({ ...textareaFocused, neurodiversityOther: false })}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}


                                            {value.healthDetail === 'Physical health' && (
                                                <div className="mt-2">
                                                    <select
                                                        name="physicalHealthDetail"
                                                        value={value.physicalHealthDetail || ''}
                                                        onChange={(e) => setValue({ ...value, physicalHealthDetail: e.target.value })}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                                                    >
                                                        <option value="">Please select...</option>
                                                        <option value="Musculoskeletal">Musculoskeletal</option>
                                                        <option value="Women's health">Women's health</option>
                                                        <option value="Chronic conditions">Chronic conditions</option>
                                                        <option value="Cancer">Cancer</option>
                                                        <option value="Voice related">Voice related</option>
                                                        <option value="Other medical issue">Other medical issue (please state)</option>
                                                    </select>
                                                    {value.physicalHealthDetail === 'Other medical issue' && (
                                                        <div className="mt-2">
                                                            <input
                                                                type="text"
                                                                id="physicalHealthOther"
                                                                name="physicalHealthOther"
                                                                className="border rounded-md py-1 px-2 w-full text-sm"
                                                                placeholder="Please specify other medical issue..."
                                                                onChange={(e) => setValue({ ...value, physicalHealthOther: e.target.value })}
                                                                value={value.physicalHealthOther || ''}
                                                                onFocus={() => setTextareaFocused({ ...textareaFocused, physicalHealthOther: true })}
                                                                onBlur={() => setTextareaFocused({ ...textareaFocused, physicalHealthOther: false })}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}


                                    {value.issueNature === 'Personal circumstances' && (
                                        <div className="p-2 bg-gray-50 rounded-md">
                                            <div className="space-y-2 pl-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="caring_responsibilities"
                                                        name="personalDetail"
                                                        value="Caring Responsibilities"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, personalDetail: e.target.value })}
                                                        checked={value.personalDetail === 'Caring Responsibilities'}
                                                    />
                                                    <label htmlFor="caring_responsibilities" className="text-sm text-gray-700">Caring Responsibilities</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="life_changes"
                                                        name="personalDetail"
                                                        value="Life Changes – Bereavement, Separation, Health Concerns Etc"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, personalDetail: e.target.value })}
                                                        checked={value.personalDetail === 'Life Changes – Bereavement, Separation, Health Concerns Etc'}
                                                    />
                                                    <label htmlFor="life_changes" className="text-sm text-gray-700">Life Changes – Bereavement, Separation, Health Concerns Etc</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="circumstances_impacting_work"
                                                        name="personalDetail"
                                                        value="Personal Circumstances Impacting Work"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, personalDetail: e.target.value })}
                                                        checked={value.personalDetail === 'Personal Circumstances Impacting Work'}
                                                    />
                                                    <label htmlFor="circumstances_impacting_work" className="text-sm text-gray-700">Personal Circumstances Impacting Work</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}


                                    {value.issueNature === 'Wellbeing concerns' && (
                                        <div className="p-2 bg-gray-50 rounded-md">
                                            <div className="space-y-2 pl-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="wellbeing_support_request"
                                                        name="wellbeingDetail"
                                                        value="Request For Wellbeing Support"
                                                        className="mr-2"
                                                        onChange={(e) => setValue({ ...value, wellbeingDetail: e.target.value })}
                                                        checked={value.wellbeingDetail === 'Request For Wellbeing Support'}
                                                    />
                                                    <label htmlFor="wellbeing_support_request" className="text-sm text-gray-700">Request For Wellbeing Support</label>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion(3)}
                            >
                                <label className="block text-sm font-medium text-gray-700">
                                    Question 3: What has happened so far? <span className="text-red-500">*</span>
                                    {isQuestionComplete(3) && <span className="ml-2 text-green-600">✓</span>}
                                </label>
                                <span className="text-gray-400">
                                    {question3Open ? '▼' : '▶'}
                                </span>
                            </div>
                            {!question3Open && getQuestionSummary(3) && (
                                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                                    {getQuestionSummary(3)}
                                </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${question3Open ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div className="p-2">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-600 mb-2">
                                                Meetings held:
                                            </label>
                                            <div className="space-y-2 ml-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="return_meeting"
                                                        name="meetingsHeld"
                                                        value="Return To Work Meeting"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, meetingsHeld: e.target.value }
                                                            delete newValue.meetingOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.meetingsHeld === 'Return To Work Meeting'}
                                                    />
                                                    <label htmlFor="return_meeting" className="text-sm text-gray-700">Return To Work Meeting</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="welfare_meeting"
                                                        name="meetingsHeld"
                                                        value="Welfare (Attendance) Meeting"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, meetingsHeld: e.target.value }
                                                            delete newValue.meetingOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.meetingsHeld === 'Welfare (Attendance) Meeting'}
                                                    />
                                                    <label htmlFor="welfare_meeting" className="text-sm text-gray-700">Welfare (Attendance) Meeting</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="hr_meeting"
                                                        name="meetingsHeld"
                                                        value="HR Capability Meeting"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, meetingsHeld: e.target.value }
                                                            delete newValue.meetingOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.meetingsHeld === 'HR Capability Meeting'}
                                                    />
                                                    <label htmlFor="hr_meeting" className="text-sm text-gray-700">HR Capability Meeting</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="informal_checkin"
                                                        name="meetingsHeld"
                                                        value="Informal Check In"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, meetingsHeld: e.target.value }
                                                            delete newValue.meetingOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.meetingsHeld === 'Informal Check In'}
                                                    />
                                                    <label htmlFor="informal_checkin" className="text-sm text-gray-700">Informal Check In</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="other_meetings"
                                                        name="meetingsHeld"
                                                        value="Other meetings"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, meetingsHeld: e.target.value }
                                                            delete newValue.meetingOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.meetingsHeld === 'Other meetings'}
                                                    />
                                                    <label htmlFor="other_meetings" className="text-sm text-gray-700">Other Relevant Meetings (Please State)</label>
                                                </div>
                                            </div>
                                            {value.meetingsHeld === 'Other meetings' && (
                                                <div className="mt-2 ml-4">
                                                    <textarea
                                                        id="meetingOther"
                                                        name="meetingOther"
                                                        rows="2"
                                                        className="border rounded-md py-2 px-4 w-full resize-vertical"
                                                        placeholder="Please specify other meetings..."
                                                        onChange={(e) => setValue({ ...value, meetingOther: e.target.value })}
                                                        value={value.meetingOther || ''}
                                                        onFocus={() => setTextareaFocused({ ...textareaFocused, meetingOther: true })}
                                                        onBlur={() => setTextareaFocused({ ...textareaFocused, meetingOther: false })}
                                                    ></textarea>
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-600 mb-2">
                                                Outcomes:
                                            </label>
                                            <div className="space-y-2 ml-4">
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="attendance_plan"
                                                        name="outcomes"
                                                        value="Attendance Plan Agreed"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, outcomes: e.target.value }
                                                            delete newValue.outcomeOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.outcomes === 'Attendance Plan Agreed'}
                                                    />
                                                    <label htmlFor="attendance_plan" className="text-sm text-gray-700">Attendance Plan Agreed</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="performance_plan"
                                                        name="outcomes"
                                                        value="Performance Management Plan Agreed"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, outcomes: e.target.value }
                                                            delete newValue.outcomeOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.outcomes === 'Performance Management Plan Agreed'}
                                                    />
                                                    <label htmlFor="performance_plan" className="text-sm text-gray-700">Performance Management Plan Agreed</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="adjustments_agreed"
                                                        name="outcomes"
                                                        value="Adjustments Agreed"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, outcomes: e.target.value }
                                                            delete newValue.outcomeOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.outcomes === 'Adjustments Agreed'}
                                                    />
                                                    <label htmlFor="adjustments_agreed" className="text-sm text-gray-700">Adjustments Agreed</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="sickness_absence"
                                                        name="outcomes"
                                                        value="Sickness Absence"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, outcomes: e.target.value }
                                                            delete newValue.outcomeOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.outcomes === 'Sickness Absence'}
                                                    />
                                                    <label htmlFor="sickness_absence" className="text-sm text-gray-700">Sickness Absence</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="escalation_policy"
                                                        name="outcomes"
                                                        value="Escalation In Line With Policy"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, outcomes: e.target.value }
                                                            delete newValue.outcomeOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.outcomes === 'Escalation In Line With Policy'}
                                                    />
                                                    <label htmlFor="escalation_policy" className="text-sm text-gray-700">Escalation In Line With Policy</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="no_improvement"
                                                        name="outcomes"
                                                        value="No Improvement"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, outcomes: e.target.value }
                                                            delete newValue.outcomeOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.outcomes === 'No Improvement'}
                                                    />
                                                    <label htmlFor="no_improvement" className="text-sm text-gray-700">No Improvement</label>
                                                </div>
                                                <div className="flex items-center">
                                                    <input
                                                        type="radio"
                                                        id="other_outcomes"
                                                        name="outcomes"
                                                        value="Other outcomes"
                                                        className="mr-2"
                                                        onChange={(e) => {
                                                            const newValue = { ...value, outcomes: e.target.value }
                                                            delete newValue.outcomeOther
                                                            setValue(newValue)
                                                        }}
                                                        checked={value.outcomes === 'Other outcomes'}
                                                    />
                                                    <label htmlFor="other_outcomes" className="text-sm text-gray-700">Other Relevant Outcomes (Please State)</label>
                                                </div>
                                            </div>
                                            {value.outcomes === 'Other outcomes' && (
                                                <div className="mt-2 ml-4">
                                                    <textarea
                                                        id="outcomeOther"
                                                        name="outcomeOther"
                                                        rows="2"
                                                        className="border rounded-md py-2 px-4 w-full resize-vertical"
                                                        placeholder="Please specify other outcomes..."
                                                        onChange={(e) => setValue({ ...value, outcomeOther: e.target.value })}
                                                        value={value.outcomeOther || ''}
                                                        onFocus={() => setTextareaFocused({ ...textareaFocused, outcomeOther: true })}
                                                        onBlur={() => setTextareaFocused({ ...textareaFocused, outcomeOther: false })}
                                                    ></textarea>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion(4)}
                            >
                                <label className="block text-sm font-medium text-gray-700">
                                    Question 4: What actions have been implemented? <span className="text-red-500">*</span>
                                    {isQuestionComplete(4) && <span className="ml-2 text-green-600">✓</span>}
                                </label>
                                <span className="text-gray-400">
                                    {question4Open ? '▼' : '▶'}
                                </span>
                            </div>
                            {!question4Open && getQuestionSummary(4) && (
                                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                                    {getQuestionSummary(4)}
                                </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${question4Open ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div className="p-2">
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="temp_adjustments"
                                                name="actionsImplemented"
                                                value="Temporary Adjustments"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, actionsImplemented: e.target.value })}
                                                checked={value.actionsImplemented === 'Temporary Adjustments'}
                                            />
                                            <label htmlFor="temp_adjustments" className="text-sm text-gray-700">Temporary Adjustments</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="perm_adjustments"
                                                name="actionsImplemented"
                                                value="Permanent Adjustments"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, actionsImplemented: e.target.value })}
                                                checked={value.actionsImplemented === 'Permanent Adjustments'}
                                            />
                                            <label htmlFor="perm_adjustments" className="text-sm text-gray-700">Permanent Adjustments</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="reduced_hours"
                                                name="actionsImplemented"
                                                value="Reduced Hours"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, actionsImplemented: e.target.value })}
                                                checked={value.actionsImplemented === 'Reduced Hours'}
                                            />
                                            <label htmlFor="reduced_hours" className="text-sm text-gray-700">Reduced Hours</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="phased_return"
                                                name="actionsImplemented"
                                                value="Phased Return To Work"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, actionsImplemented: e.target.value })}
                                                checked={value.actionsImplemented === 'Phased Return To Work'}
                                            />
                                            <label htmlFor="phased_return" className="text-sm text-gray-700">Phased Return To Work</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="redeployment"
                                                name="actionsImplemented"
                                                value="Redeployment"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, actionsImplemented: e.target.value })}
                                                checked={value.actionsImplemented === 'Redeployment'}
                                            />
                                            <label htmlFor="redeployment" className="text-sm text-gray-700">Redeployment</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="other_actions"
                                                name="actionsImplemented"
                                                value="Other"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, actionsImplemented: e.target.value })}
                                                checked={value.actionsImplemented === 'Other'}
                                            />
                                            <label htmlFor="other_actions" className="text-sm text-gray-700">Other (Please State)</label>
                                        </div>
                                    </div>
                                    {value.actionsImplemented === 'Other' && (
                                        <div className="mt-2 ml-6">
                                            <textarea
                                                id="actionsOther"
                                                name="actionsOther"
                                                rows="2"
                                                className="border rounded-md py-2 px-4 w-full resize-vertical"
                                                placeholder="Please specify other actions..."
                                                onChange={(e) => setValue({ ...value, actionsOther: e.target.value })}
                                                value={value.actionsOther || ''}
                                                onFocus={() => setTextareaFocused({ ...textareaFocused, actionsOther: true })}
                                                onBlur={() => setTextareaFocused({ ...textareaFocused, actionsOther: false })}
                                            ></textarea>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion(5)}
                            >
                                <label className="block text-sm font-medium text-gray-700">
                                    Question 5: What is the school's intended outcome? <span className="text-red-500">*</span>
                                    {isQuestionComplete(5) && <span className="ml-2 text-green-600">✓</span>}
                                </label>
                                <span className="text-gray-400">
                                    {question5Open ? '▼' : '▶'}
                                </span>
                            </div>
                            {!question5Open && getQuestionSummary(5) && (
                                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                                    {getQuestionSummary(5)}
                                </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${question5Open ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div className="p-2">
                                    <div className="space-y-2">
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="fitness_advice"
                                                name="intendedOutcome"
                                                value="Advice On Fitness For Work"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Advice On Fitness For Work'}
                                            />
                                            <label htmlFor="fitness_advice" className="text-sm text-gray-700">Advice On Fitness For Work</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="adjustments_advice"
                                                name="intendedOutcome"
                                                value="Advice On Adjustments"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Advice On Adjustments'}
                                            />
                                            <label htmlFor="adjustments_advice" className="text-sm text-gray-700">Advice On Adjustments</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="attendance_advice"
                                                name="intendedOutcome"
                                                value="Advice On Attendance Management"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Advice On Attendance Management'}
                                            />
                                            <label htmlFor="attendance_advice" className="text-sm text-gray-700">Advice On Attendance Management</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="performance_advice"
                                                name="intendedOutcome"
                                                value="Advice On Performance Management"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Advice On Performance Management'}
                                            />
                                            <label htmlFor="performance_advice" className="text-sm text-gray-700">Advice On Performance Management</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="wellbeing_support"
                                                name="intendedOutcome"
                                                value="Wellbeing Support For Employee"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Wellbeing Support For Employee'}
                                            />
                                            <label htmlFor="wellbeing_support" className="text-sm text-gray-700">Wellbeing Support For Employee</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="workplace_risks"
                                                name="intendedOutcome"
                                                value="Review Of Workplace Risks"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Review Of Workplace Risks'}
                                            />
                                            <label htmlFor="workplace_risks" className="text-sm text-gray-700">Review Of Workplace Risks</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="behavior_support"
                                                name="intendedOutcome"
                                                value="Support With Behavior"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Support With Behavior'}
                                            />
                                            <label htmlFor="behavior_support" className="text-sm text-gray-700">Support With Behavior</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="info_only"
                                                name="intendedOutcome"
                                                value="Information Only"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Information Only'}
                                            />
                                            <label htmlFor="info_only" className="text-sm text-gray-700">Information Only</label>
                                        </div>
                                        <div className="flex items-center">
                                            <input
                                                type="radio"
                                                id="unsure"
                                                name="intendedOutcome"
                                                value="Unsure"
                                                className="mr-2"
                                                onChange={(e) => setValue({ ...value, intendedOutcome: e.target.value })}
                                                checked={value.intendedOutcome === 'Unsure'}
                                            />
                                            <label htmlFor="unsure" className="text-sm text-gray-700">Unsure</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion(6)}
                            >
                                <label className="block text-sm font-medium text-gray-700">
                                    Question 6: Any other information
                                </label>
                                <span className="text-gray-400">
                                    {question6Open ? '▼' : '▶'}
                                </span>
                            </div>
                            {!question6Open && getQuestionSummary(6) && (
                                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                                    {getQuestionSummary(6)}
                                </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${question6Open ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div className="p-2">
                                    <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700">
                                        Any other information
                                    </label>
                                    <textarea
                                        id="additionalInfo"
                                        name="additionalInfo"
                                        rows="4"
                                        className="border rounded-md py-2 px-4 w-full resize-vertical mt-2"
                                        placeholder="Please provide any additional information that may be relevant..."
                                        onChange={(e) => setValue({ ...value, additionalInfo: e.target.value })}
                                        value={value.additionalInfo || ''}
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        {/* Purpose of Occupational Health Referral Section */}
                        <div className="mt-6 border border-gray-200 rounded-lg">
                            <div
                                className="flex items-center justify-between p-2 cursor-pointer bg-gray-50 rounded-t-lg"
                                onClick={() => toggleQuestion('employerConsent')}
                            >
                                <label className="block text-sm font-medium text-gray-700">
                                    Employer Consent <span className="text-red-500">*</span>
                                    {isPurposeComplete() && <span className="ml-2 text-green-600">✓</span>}
                                </label>
                                <span className="text-gray-400">
                                    {employerConsentOpen ? '▼' : '▶'}
                                </span>
                            </div>
                            {!employerConsentOpen && getPurposeSummary() && (
                                <div className="px-4 py-2 bg-gray-50 border-t text-sm text-gray-600">
                                    {getPurposeSummary()}
                                </div>
                            )}
                            <div className={`overflow-hidden transition-all duration-700 ease-in-out ${employerConsentOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'}`}>
                                <div className="p-4">
                                    <div className="text-sm text-gray-700 mb-4">
                                        <p className="mb-2">The primary purpose of this assessment is to:</p>
                                        <ul className="list-disc pl-5 space-y-1">
                                            <li>Provide independent and impartial advice to my Employer regarding my fitness for work</li>
                                            <li>Assess the potential effect of my work on my health, and vice versa.</li>
                                            <li>Suggest any adjustments or support measures for my Employer to consider in line with the Equality Act 2010</li>
                                            <li>Advise on any estimated timescales for my return to work duties, if applicable</li>
                                        </ul>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex items-start">
                                            <input
                                                type="checkbox"
                                                id="purposeExplained"
                                                checked={purposeExplained}
                                                onChange={(e) => setPurposeExplained(e.target.checked)}
                                                required
                                                className="mt-1 mr-3"
                                            />
                                            <label htmlFor="purposeExplained" className="text-sm text-gray-700">
                                                I confirm that the reason and purpose of this Occupational Health referral have been explained to the employee by the Employer. <span className="text-red-500">*</span>
                                            </label>
                                        </div>

                                        <div className="flex items-start">
                                            <input
                                                type="checkbox"
                                                id="questionsAware"
                                                checked={questionsAware}
                                                onChange={(e) => setQuestionsAware(e.target.checked)}
                                                required
                                                className="mt-1 mr-3"
                                            />
                                            <label htmlFor="questionsAware" className="text-sm text-gray-700">
                                                I confirm that the employee is aware of the questions asked to Occupational Health and that the report will include responses to these questions. <span className="text-red-500">*</span>
                                            </label>
                                        </div>

                                        <div className="flex items-start">
                                            <input
                                                type="checkbox"
                                                id="abandonment"
                                                checked={abandonment}
                                                onChange={(e) => setAbandonment(e.target.checked)}
                                                required
                                                className="mt-1 mr-3"
                                            />
                                            <label htmlFor="abandonment" className="text-sm text-gray-700">
                                                I am aware that if the employee is not aware of the reasons for the Occupational Health referral or the questions listed by their employer, the assessment may have to be abandoned and the appointment will be charged. <span className="text-red-500">*</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="text-xs mt-4">
                            <span className="text-red-500">*</span> Denotes required fields.
                        </div>
                    </div>
                    <div className="px-4 mt-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-lg font-semibold leading-6 text-[#02ADAD]">
                                    Your Details
                                </div>
                                <div className="text-xs text-[#02ADAD]">
                                    These will be shown on the request
                                </div>
                            </div>
                            {orgInfo.type === 'trust' && (
                                <div
                                    onClick={toggleEditMode}
                                    className="border flex items-center rounded h-[35px] w-1/5 border-[#02ADAD] cursor-pointer text-sm"
                                >
                                    <div className="text-[#02ADAD] h-full flex justify-center w-[75%] items-center font-medium">
                                        {!editMode ? 'Edit Details' : 'Cancel'}
                                    </div>
                                    <div className="bg-[#02ADAD] text-white h-full flex  w-[25%] justify-center items-center">
                                        <FontAwesomeIcon
                                            icon={!editMode ? faEdit : faTimes}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-4 flex">
                            <div className="text-gray-600 mr-2">Name:</div>
                            <div className="font-bold">
                                {auth.user.contact_name ?? ''}
                            </div>
                        </div>

                        <div className="mt-2 flex">
                            <div className="text-gray-600 mr-2">Email:</div>
                            <div className="font-bold">
                                {auth.user.contact_email ?? ''}
                            </div>
                        </div>

                        <div className="mt-2 flex items-center">
                            <div className="text-gray-600 mr-2">School Name:</div>
                            {editMode ? (
                                <input
                                    type="text"
                                    defaultValue={schoolName}
                                    onChange={(e) => setSchoolName(e.target.value)}
                                    className="font-bold border rounded-md border-gray-300 px-2 py-1 w-3/4"
                                />
                            ) : (
                                <div className="font-bold">{schoolName}</div>
                            )}
                        </div>

                        <div className="mt-2 flex items-center">
                            <div className="text-gray-600 mr-2">
                                School Postcode:
                            </div>
                            {editMode ? (
                                <input
                                    type="text"
                                    defaultValue={schoolPostcode}
                                    onChange={(e) => setSchoolPostcode(e.target.value)}
                                    className="font-bold border rounded-md border-gray-300 px-2 py-1"
                                />
                            ) : (
                                <div className="font-bold">{schoolPostcode}</div>
                            )}
                        </div>
                    </div>
                </div >

                <div className=" hover:shadow-md w-11/12 mx-auto my-4 relative">
                    {showValidationTooltip && (
                        <div className="absolute -top-12 left-1/2 transform -translate-x-1/2 bg-red-600 text-white text-sm px-3 py-2 rounded-md shadow-lg z-10 whitespace-nowrap">
                            <div className="text-center">Please complete all required fields</div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-red-600"></div>
                        </div>
                    )}
                    <button
                        type="submit"
                        className={`border flex items-center rounded h-[45px] w-full ${isFormValid()
                            ? 'border-[#02ADAD] cursor-pointer'
                            : 'border-gray-400'
                            }`}
                        onClick={submitRequest}
                    >
                        <div className={`h-full flex justify-center items-center px-10 font-medium w-full ${isFormValid() ? 'text-[#02ADAD]' : 'text-gray-500'}`}>
                            {requestData ? 'Update Request' : 'Submit OH Request'}
                        </div>
                        <div className={`text-white h-full flex w-[45px] justify-center items-center ${isFormValid() ? 'bg-[#02ADAD]' : 'bg-gray-400'}`}>
                            <FontAwesomeIcon icon={requestData ? faEdit : faPlus} />
                        </div>
                    </button>
                </div>
            </form >
        </>
    )
}

export default OHRequestForm